import {
  Box,
  Button,
  createStyles,
  Group,
  HoverCard,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import React from 'react';
import { ConnectDragSource } from 'react-dnd';

import {
  DeviceType,
  useSpace,
  useUnsnoozeDevice,
} from '@portals/api/organizations';
import { DeviceAvatar, DeviceStatusBadge } from '@portals/framework';
import { useOpenRouteModal } from '@portals/framework/route-modals';
import { ReactComponent as Drag } from '@portals/icons/linear/drag.svg';
import { ReactComponent as Notification } from '@portals/icons/linear/notification.svg';
import { prettyTime, suppressPropagation } from '@portals/utils';

import { DeviceRelationsIndicator } from './DeviceRelationsIndicator';
import { canEdit } from '../../../../../../../lib/access';
import { DeviceIncidentsCounterWithIcon } from '../../../../../../components/DeviceCounters';
import { DeviceCardMenu } from '../device-card-menu/DeviceCardMenu';

interface DeviceCardLayoutProps {
  device: DeviceType;
  dragRef?: ConnectDragSource;
  isDragging?: boolean;
  isPreview?: boolean;
  width?: number;
}

interface ParamsProps {
  isDragging: boolean;
  isPreview?: boolean;
  width: number;
}

export function DeviceCardLayout({
  device,
  dragRef,
  isDragging,
  isPreview = false,
  width,
}: DeviceCardLayoutProps) {
  const { classes, cx } = useStyles({ isDragging, isPreview, width });

  const unsnoozeDevice = useUnsnoozeDevice(device.id);
  const openRouteModal = useOpenRouteModal();
  const space = useSpace(device.space_id);

  return (
    <Paper
      ref={dragRef && canEdit(space) ? dragRef : null}
      className={classes.staticContainer}
      withBorder={!isDragging}
      onClick={() =>
        openRouteModal({ modalId: 'device', pathParams: [device.id] })
      }
    >
      <Stack
        p="xl"
        spacing="md"
        className={cx({
          [classes.draggingContainer]: isDragging && !isPreview,
          [classes.preview]: isDragging && isPreview,
        })}
      >
        <Group
          className={classes.avatarWrapper}
          align="start"
          position="apart"
          spacing="xs"
          noWrap
        >
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <DeviceAvatar
              img={device.model_settings?.media?.img}
              icon={device.model_settings?.icon}
              className={classes.avatar}
            />
          </Box>

          <Box className="device-card-menu">
            <DeviceCardMenu device={device} />
          </Box>

          <DeviceStatusBadge status={device.status} />
        </Group>

        <Stack spacing={2}>
          <Text
            className={classes.name}
            data-testid={`device-card-title-${device.name}`}
          >
            {device.name}
          </Text>
          <Text className={classes.model}>{device.partner.model}</Text>
        </Stack>

        <Group>
          <DeviceIncidentsCounterWithIcon device={device} />

          <DeviceRelationsIndicator device={device} />

          {device.snoozed_until ? (
            <HoverCard withinPortal>
              <HoverCard.Target>
                <Notification width={18} height={18} />
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Stack spacing={4}>
                  <Text color="blue_gray.8">
                    Incident tracking is off until
                  </Text>
                  <Text color="blue_gray.8" weight={600}>
                    {prettyTime(device.snoozed_until)}
                  </Text>
                  <Button
                    mt="xs"
                    variant="default"
                    loading={unsnoozeDevice.isLoading}
                    onClick={suppressPropagation(() => unsnoozeDevice.mutate())}
                  >
                    Click to turn it on
                  </Button>
                </Stack>
              </HoverCard.Dropdown>
            </HoverCard>
          ) : null}
        </Group>
      </Stack>

      {canEdit(space) && !isDragging ? (
        <Drag className={cx('device-drag-icon', classes.dragIcon)} />
      ) : null}
    </Paper>
  );
}

const useStyles = createStyles((theme, params: ParamsProps) => ({
  staticContainer: {
    cursor: 'grab',
    width: params.width,
    backgroundColor:
      params.isDragging && !params.isPreview
        ? theme.colors.gray[0]
        : theme.colors.white,
    position: 'relative',
    borderRadius: 20,
    transition: 'filter 150ms linear',
    height: '100%',
    opacity: 1,

    '.device-card-menu, .device-drag-icon': {
      opacity: 0,
      transition: 'opacity 150ms ease-in-out',
    },

    '&:hover': {
      filter: params.isDragging
        ? 'none'
        : 'drop-shadow(0px 4px 10px rgba(38, 50, 56, 0.13))',

      '.device-card-menu, .device-drag-icon': {
        opacity: 1,
      },
    },
  },
  preview: {
    boxShadow: params.isDragging ? '0 45px 30px rgba(0,0,0,0.1)' : 'unset',
    borderRadius: 20,
    transition: 'filter 150ms linear',

    '.device-card-menu, .device-drag-icon': {
      opacity: 0,
      transition: 'opacity 150ms ease-in-out',
    },

    '&:hover': {
      filter: 'drop-shadow(0px 4px 10px rgba(38, 50, 56, 0.13))',

      '.device-card-menu, .device-drag-icon': {
        opacity: 1,
      },
    },
  },
  draggingContainer: {
    backgroundColor: theme.colors.gray[1],
    position: 'relative',
    height: '100%',
    opacity: 0,
  },
  name: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.blue_gray[8],
    fontWeight: 500,
  },
  model: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.blue_gray[3],
    fontWeight: 300,
  },
  avatarWrapper: {
    position: 'relative',
  },
  childrenIcon: {
    color: theme.colors.blue_gray[4],
  },
  avatar: {
    width: 70,
    height: 70,
    minWidth: 70,
    minHeight: 70,
    pointerEvents: 'none',
  },
  dragIcon: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    color: theme.colors.gray[4],
  },
}));
